import React, { useCallback, useEffect, useState } from 'react';
import {
  IonContent,
  IonSpinner,
  IonText,
  IonIcon,
  IonButton,
  IonAlert,
  useIonViewWillEnter,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { Capacitor } from '@capacitor/core';
import { 
  alertCircleOutline, 
  add,
  banOutline,
  createOutline,
  gridOutline,
  checkmarkCircleOutline,
  lockClosedOutline,
  lockOpenOutline,
} from 'ionicons/icons';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import PageHeaderBar from '@/components/common/PageHeaderBar';
import BulkActionsBar, { BulkActionsBarAction } from '@/components/common/BulkActionsBar';
import ActionAccordion from '@/components/common/accordion/ActionAccordion';
import GroupModal, { GroupData, GroupModalMode } from '@/components/planning/modal/GroupModal';
import LineItemModal, { LineItemSaveData } from '@/components/planning/modal/LineItemModal';
import InspectionPlanModal from '@/components/planning/modal/InspectionPlanModal';
import PlanningGridView from '@/components/planning/desktop/PlanningGridView';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import { CreateLineItemRequest, UpdateLineItemRequest } from '@/types/request/InspectionPlanningRequests';
import { FileAttachment } from '@/types/model/inspection-modal';
import { 
  planningService, 
  PlanningGroup, 
  PlanningLineItem, 
  InspectionPlanItem,
  PlanningFilters,
  BulkActionSelection,
  BulkAction,
  PlanningGroupGridRow,
  PlanningLineItemGridRow,
  PlanningInspectionPlanGridRow
} from '@/services/PlanningService';
import { CreateGroupRequest, UpdateGroupRequest } from '@/types/request/InspectionPlanningRequests';
import { apiService } from '@/services/ApiService';
import permissionService from '@/services/PermissionService';
import lookupDataService from '@/services/data/LookupDataService';
import { ActionAccordionRowData } from '@/types/model/actionAccordion';
import { setRowSelection } from '@/services/util/AccordionUtils';
import { buildPlanningActionAccordionRows } from '@/services/util/adapters/PlanningActionAccordionAdapter';
import { PageHeaderIconAction } from '@/types/model/pageHeaderBar';
import {
  buildPlanningFilterControls,
  buildPlanningFilterSummary,
  mapPlanningControlsToFilters,
} from '@/services/util/adapters/PlanningPageHeaderAdapter';
import { filterPlanningGroupsBySearch } from '@/services/util/planning/planningSearch';
import { PlanningViewMode, getStoredPlanningViewMode, setStoredPlanningViewMode } from '@/services/util/planning/planningViewPreference';
import { buildAttachmentOperations } from '@/services/util/attachmentOperations';
import {
  areNumberSetsEqual,
  findPlanningGroupIdForLineItem,
  getSelectedVisibleLineItemCount,
  getVisibleLineItemIds,
  mapPlanningGroupsToAvailableGroups,
  mapPlanningGroupsToGroupData,
  mergePlanningLineItems,
  truncateText,
} from '@/services/util/planningPageUtils';

const Planning: React.FC = () => {
  usePageTitle('Planning');

  const [data, setData] = useState<PlanningGroup[]>([]);
  const [viewData, setViewData] = useState<PlanningGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [actionAccordionRows, setActionAccordionRows] = useState<ActionAccordionRowData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Group Modal state
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState<GroupModalMode>('add');
  const [selectedGroupData, setSelectedGroupData] = useState<GroupData | null>(null);
  
  // Line Item Modal state
  const [lineItemModalOpen, setLineItemModalOpen] = useState(false);
  const [lineItemModalMode, setLineItemModalMode] = useState<'add' | 'view' | 'edit'>('add');
  const [selectedLineItemData, setSelectedLineItemData] = useState<any>(null);
  const [existingFiles, setExistingFiles] = useState<FileResponse[]>([]);
  
  // Inspection Plan Modal state
  const [inspectionPlanModalOpen, setInspectionPlanModalOpen] = useState(false);
  const [inspectionPlanModalMode, setInspectionPlanModalMode] = useState<'view' | 'edit'>('view');
  const [selectedInspectionPlanData, setSelectedInspectionPlanData] = useState<any>(null);
  
  const [filters, setFilters] = useState<PlanningFilters>({
    showAll: true,
    showActiveLineItems: true,
    showInactiveLineItems: true,
    showActiveInspectionPlans: true,
    showInactiveInspectionPlans: true,
    showLockedInspectionPlans: true,
    showUnlockedInspectionPlans: true,
  });

  const [selection, setSelection] = useState<BulkActionSelection>({
    selectedGroups: new Set(),
    selectedLineItems: new Set(),
  });
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [pendingDeactivateIds, setPendingDeactivateIds] = useState<number[]>([]);
  const [runningHeaderActionId, setRunningHeaderActionId] = useState<string | null>(null);
  const [lookupReady, setLookupReady] = useState(() => lookupDataService.isLoaded());
  const canEditGroup = lookupReady && permissionService.canEditGroup();
  const canAddLineItem = lookupReady && permissionService.canAddLineItem();
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState<PlanningViewMode>(() => getStoredPlanningViewMode());

  useEffect(() => {
    let isMounted = true;

    const initializeLookupData = async () => {
      try {
        await lookupDataService.initialize();
        if (isMounted) {
          setLookupReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize planning lookup data:', error);
      }
    };

    void initializeLookupData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const loadPlanningData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [planningData, contractFiles] = await Promise.all([
        planningService.getGroupedPlanningData(),
        apiService.getContractFiles(),
      ]);
      setData(planningData);
      setExistingFiles(contractFiles);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load planning data';
      setError(errorMessage);
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
      console.error('Error loading planning data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterData = () => {
      try {
        const baseFilteredData = filters.showAll
          ? data
          : planningService.filterPlanningGroups(data, filters);

        const filteredData = filterPlanningGroupsBySearch(baseFilteredData, searchQuery);

        setViewData(filteredData);
        
        if (filteredData.length === 0) {
          setError('No planning results found matching the current filters and search.');
        } else {
          setError(null);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to filter planning data';
        setError(errorMessage);
        setToastMessage(errorMessage);
        setToastVariant('error');
        setShowToast(true);
        console.error('Error filtering planning data:', err);
      }
    };
    filterData();
  }, [filters, data, searchQuery]);

  useIonViewWillEnter(() => {
    loadPlanningData();
  });

  const filterControls = buildPlanningFilterControls(filters);

  const handleAccordionSelectionChange = useCallback((selectedRows: ActionAccordionRowData[]) => {
    const selectedGroups = new Set<number>();
    const selectedLineItems = new Set<number>();

    for (const row of selectedRows) {
      if (row.type === 'Group') {
        selectedGroups.add(row.id);
      }

      if (row.type === 'LineItem') {
        selectedLineItems.add(row.id);
      }
    }

    setSelection((currentSelection) => {
      if (
        areNumberSetsEqual(currentSelection.selectedGroups, selectedGroups) &&
        areNumberSetsEqual(currentSelection.selectedLineItems, selectedLineItems)
      ) {
        return currentSelection;
      }

      return { selectedGroups, selectedLineItems };
    });
  }, []);

  const handleBulkAction = async (action?: BulkAction) => {
    const actionToUse = action ?? 'activate-line-items';
    const selectedIds = Array.from(selection.selectedLineItems);
    
    if (selectedIds.length === 0) {
      setToastMessage('No line items selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    setRunningHeaderActionId(actionToUse);

    try {
      switch (actionToUse) {
        case 'activate-line-items': {
          const result = await planningService.activateLineItems(selectedIds);
          if (result.failed.length === 0) {
            setToastMessage(`${result.successIds.length} line item(s) activated`);
            setToastVariant('success');
          } else {
            setToastMessage(`Activated ${result.successIds.length}/${selectedIds.length}. Failed: ${result.failed.length}`);
            setToastVariant('warning');
          }
          break;
        }
        case 'deactivate-line-items':
          setRunningHeaderActionId(null);
          setPendingDeactivateIds(selectedIds);
          setShowDeactivateConfirm(true);
          return;
        case 'activate-inspection-plans': {
          const planIds = planningService.getInspectionPlanIdsForSelectedLineItems(selection.selectedLineItems, viewData);
          if (planIds.length === 0) {
            setToastMessage('No inspection plans found for the selected line items');
            setToastVariant('warning');
            setShowToast(true);
            return;
          }
          await apiService.activateDeactivateInspectionPlan(planIds, []);
          setToastMessage(`${planIds.length} inspection plan(s) activated`);
          setToastVariant('success');
          break;
        }
        case 'deactivate-inspection-plans': {
          const planIds = planningService.getInspectionPlanIdsForSelectedLineItems(selection.selectedLineItems, viewData);
          if (planIds.length === 0) {
            setToastMessage('No inspection plans found for the selected line items');
            setToastVariant('warning');
            setShowToast(true);
            return;
          }
          await apiService.activateDeactivateInspectionPlan([], planIds);
          setToastMessage(`${planIds.length} inspection plan(s) deactivated`);
          setToastVariant('success');
          break;
        }
        case 'lock-inspection-plans': {
          const planIds = planningService.getInspectionPlanIdsForSelectedLineItems(selection.selectedLineItems, viewData);
          if (planIds.length === 0) {
            setToastMessage('No inspection plans found for the selected line items');
            setToastVariant('warning');
            setShowToast(true);
            return;
          }
          await apiService.lockUnlockInspectionPlan(planIds, [], '');
          setToastMessage(`${planIds.length} inspection plan(s) locked`);
          setToastVariant('success');
          break;
        }
        case 'unlock-inspection-plans': {
          const planIds = planningService.getInspectionPlanIdsForSelectedLineItems(selection.selectedLineItems, viewData);
          if (planIds.length === 0) {
            setToastMessage('No inspection plans found for the selected line items');
            setToastVariant('warning');
            setShowToast(true);
            return;
          }
          await apiService.lockUnlockInspectionPlan([], planIds, '');
          setToastMessage(`${planIds.length} inspection plan(s) unlocked`);
          setToastVariant('success');
          break;
        }
      }
      setShowToast(true);
      await refreshAfterBulkAction();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setRunningHeaderActionId(null);
    }
  };

  const refreshAfterBulkAction = async () => {
    // Refresh data after action
    await refreshPlanningData();
    // Clear selection
    setSelection({ selectedGroups: new Set(), selectedLineItems: new Set() });
  };

  const refreshPlanningData = async () => {
    await loadPlanningData();
  };

  const refreshAffectedGroupLineItems = async (
    groupId: number,
    affectedLineItemId?: number,
    reloadInspectionPlan = false
  ): Promise<void> => {
    const refreshedLineItems = await planningService.getLineItemsForGroup(groupId);
    const refreshedInspectionPlan = affectedLineItemId && reloadInspectionPlan
      ? await planningService.getInspectionPlanForLineItem(affectedLineItemId)
      : undefined;

    setData(prevData =>
      prevData.map(group => {
        if (group.id !== groupId) {
          return group;
        }

        const existingLineItems = group.lineItems ?? [];
        const mergedLineItems = mergePlanningLineItems(existingLineItems, refreshedLineItems).map((lineItem, index) => {
          const refreshedLineItem = refreshedLineItems[index];
          return {
            ...lineItem,
            inspectionPlan: refreshedLineItem.id === affectedLineItemId && reloadInspectionPlan
              ? refreshedInspectionPlan ?? null
              : lineItem.inspectionPlan ?? refreshedLineItem.inspectionPlan ?? null
          };
        });

        return {
          ...group,
          lineItems: mergedLineItems
        };
      })
    );
  };

  const handleConfirmDeactivateLineItems = async (reason: string) => {
    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setToastMessage('Reason is required to inactivate line items');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    if (pendingDeactivateIds.length === 0) {
      setToastMessage('No line items selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    setRunningHeaderActionId('deactivate-line-items');

    try {
      const result = await planningService.inactivateLineItems(pendingDeactivateIds, trimmedReason);
      if (result.failed.length === 0) {
        setToastMessage(`${result.successIds.length} line item(s) deactivated`);
        setToastVariant('success');
      } else {
        setToastMessage(`Deactivated ${result.successIds.length}/${pendingDeactivateIds.length}. Failed: ${result.failed.length}`);
        setToastVariant('warning');
      }
      setShowToast(true);
      await refreshAfterBulkAction();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setRunningHeaderActionId(null);
      setPendingDeactivateIds([]);
    }
  };

  // Group Modal handlers
  const handleAddGroup = () => {
    setGroupModalMode('add');
    setSelectedGroupData(null);
    setGroupModalOpen(true);
  };

  const handleViewGroup = (group: PlanningGroup) => {
    const groupData: GroupData = {
      id: group.id,
      identifier: group.groupIdentifier,
      title: group.title
    };
    setSelectedGroupData(groupData);
    setGroupModalMode(permissionService.canEditGroup() ? 'edit' : 'view');
    setGroupModalOpen(true);
  };

  const handleGroupModalClose = () => {
    setGroupModalOpen(false);
    setSelectedGroupData(null);
  };

  const handleGroupSave = async (groupData: GroupData, mode: GroupModalMode) => {
    try {
      if (mode === 'add') {
        const createRequest: CreateGroupRequest = {
          Identifier: groupData.identifier,
          Title: groupData.title,
        };
        await planningService.createGroup(createRequest);
      } else if (mode === 'edit' && groupData.id !== undefined) {
        const updateRequest: UpdateGroupRequest = {
          Id: groupData.id,
          Identifier: groupData.identifier,
          Title: groupData.title,
        };
        await planningService.updateGroup(updateRequest);
      }
      await lookupDataService.refreshGroups();
      await refreshPlanningData();
      setToastMessage('Data refreshed successfully');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error saving line item:', error);
      setToastMessage(error instanceof Error ? error.message : 'Failed to save line item');
      setToastVariant('error');
      setShowToast(true);
      throw error;
    }
  };

  // Line Item Modal handlers
  const handleAddLineItem = (groupId: number) => {
    setLineItemModalMode('add');
    setSelectedLineItemData({ groupId }); // Set the initial group ID
    setLineItemModalOpen(true);
  };

  const handleLineItemModalClose = () => {
    setLineItemModalOpen(false);
    setSelectedLineItemData(null);
  };

  const handleLineItemSave = async (saveData: LineItemSaveData) => {
    try {
      const group = data.find(item => item.id === saveData.formData.groupId);
      const sectionId = group?.id;

      if (!sectionId) {
        throw new Error('Selected group not found');
      }

      const lineItemAlreadyExists = await planningService.checkLineItemExists({
        Id: saveData.initialData?.id ?? 0,
        SectionId: sectionId,
        Paragraph: saveData.formData.paragraph.trim() || undefined,
        SubParagraph: saveData.formData.subParagraph.trim() || undefined,
        Remainder: saveData.formData.remainder.trim() || undefined,
      });

      if (lineItemAlreadyExists) {
        throw new Error('A line item with this identifier already exists.');
      }

      const uploadedFileIds = new Map<FileAttachment, number>();
      for (const attachment of saveData.attachments) {
        if (!attachment.fileId && attachment.file) {
          const uploadResult = await apiService.uploadFile(attachment);
          uploadedFileIds.set(attachment, uploadResult.FileId);
        }
      }

      const attachmentOperations = buildAttachmentOperations({
        attachments: saveData.attachments,
        originalAttachments: saveData.originalAttachments,
        uploadedFileIds,
      });

      const formData = saveData.formData;
      if (saveData.mode === 'add') {
        const createRequest: CreateLineItemRequest = {
          Title: formData.title,
          SectionId: sectionId,
          Paragraph: formData.paragraph,
          SubParagraph: formData.subParagraph,
          Remainder: formData.remainder,
          MajorRequirement: formData.majorRequirement,
          RelatedInfo: formData.relatedInfo,
          EstimatedQuantity: formData.estimatedQuantity,
          PerformanceStandard: formData.performanceStandard,
          Attachments: attachmentOperations,
        };
        await planningService.createLineItem(createRequest);
      } else if (saveData.initialData?.id) {
        const updateRequest: UpdateLineItemRequest = {
          Id: saveData.initialData.id,
          LineItem: saveData.initialData.lineItem || formData.lineItem,
          Title: formData.title,
          LastUpdate: saveData.initialData.lastUpdated || new Date().toISOString(),
          Paragraph: formData.paragraph,
          SubParagraph: formData.subParagraph,
          Remainder: formData.remainder,
          MajorRequirement: formData.majorRequirement,
          RelatedInfo: formData.relatedInfo,
          EstimatedQuantity: formData.estimatedQuantity,
          PerformanceStandard: formData.performanceStandard,
          ModificationStatus: saveData.initialData.modificationStatus || '',
          Attachments: attachmentOperations,
          AttachmentCount: saveData.initialData.attachmentCount ?? saveData.attachments.length,
          IsActive: saveData.initialData.isActive ?? true,
          SectionId: String(group?.id ?? sectionId),
        };
        await planningService.updateLineItem(updateRequest);
      }

      const groupId = saveData.formData.groupId as number | undefined;
      const lineItemId = saveData.initialData?.id as number | undefined;

      if (groupId) {
        await refreshAffectedGroupLineItems(groupId, lineItemId, false);
      } else {
        await refreshPlanningData();
      }

      setToastMessage('Line item saved successfully');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error saving line item:', error);
      setToastMessage(error instanceof Error ? error.message : 'Failed to save line item');
      setToastVariant('error');
      setShowToast(true);
      throw error;
    }
  };

  const handleInspectionPlanModalClose = () => {
    setInspectionPlanModalOpen(false);
    setSelectedInspectionPlanData(null);
  };

  const handleInspectionPlanSave = async () => {
    try {
      const lineItemId = selectedInspectionPlanData?.lineItemId as number | undefined;
      const groupId = lineItemId ? findPlanningGroupIdForLineItem(data, lineItemId) : null;

      if (lineItemId && groupId) {
        await refreshAffectedGroupLineItems(groupId, lineItemId, true);
      } else {
        await refreshPlanningData();
      }

      setToastMessage('Inspection plan saved successfully');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error saving inspection plan:', error);
      setToastMessage(error instanceof Error ? error.message : 'Failed to save inspection plan');
      setToastVariant('error');
      setShowToast(true);
      throw error;
    }
  };

  const handleInspectionPlanEdit = (id: number) => {
    for (const group of data) {
      for (const lineItem of group.lineItems || []) {
        if (lineItem.inspectionPlan?.id === id) {
          handleViewInspectionPlan(lineItem.inspectionPlan, lineItem, group);
          return;
        }
      }
    }
  };

  const handlePlanningAccordionRowExpand = (_row: ActionAccordionRowData) => {
    // Planning line item content is eager loaded; no additional fetch is required when expanding.
  };

  useEffect(() => {
    setActionAccordionRows((prevRows) =>
      buildPlanningActionAccordionRows({
        groups: viewData,
        previousRows: prevRows,
        canAddLineItem,
        onEditGroup: handleViewGroup,
        onAddLineItem: handleAddLineItem,
        onEditLineItem: handleViewLineItem,
        renderLineItemContent: (lineItem) => renderInspectionPlan(lineItem.inspectionPlan || null),
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewData, canAddLineItem, canEditGroup]);

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '2rem', 
          gap: '1rem' 
        }}>
          <IonSpinner name="crescent" />
          <IonText color="medium">
            <p>Loading planning data...</p>
          </IonText>
        </div>
      );
    }

    if (error && viewData.length === 0) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '2rem', 
          gap: '1rem' 
        }}>
          <IonIcon icon={alertCircleOutline} style={{ fontSize: '3rem', color: 'var(--ion-color-danger)' }} />
          <IonText color="medium">
            <h3>No Results Found</h3>
            <p style={{ textAlign: 'center' }}>{error}</p>
          </IonText>
        </div>
      );
    }

    return (
      <ActionAccordion
        rows={actionAccordionRows}
        onRowsChange={setActionAccordionRows}
        selectionMode="cascade-visible"
        onSelectionChange={handleAccordionSelectionChange}
        onRowExpand={handlePlanningAccordionRowExpand}
      />
    );
  };

  const handleViewInspectionPlan = (inspectionPlan: InspectionPlanItem, lineItem: PlanningLineItem, _group: PlanningGroup) => {
    const canEdit = permissionService.canEditInspectionPlan(inspectionPlan.isLocked);
    setInspectionPlanModalMode(canEdit ? 'edit' : 'view');
    setSelectedInspectionPlanData({
      id: inspectionPlan.id,
      lineItemId: lineItem.id,
      lineItemTitle: lineItem.title,
      lineItemIdentifier: lineItem.lineItem,
      primaryValue: inspectionPlan.primaryValue,
      alternateValue: inspectionPlan.alternateValue,
      timeToInspect: inspectionPlan.timeToInspect,
      inspectionMethodId: inspectionPlan.inspectionMethodId,
      expertiseAreaId: inspectionPlan.expertiseAreaId,
      sampleSize: inspectionPlan.sampleSize,
      sampleSelection: inspectionPlan.sampleSelection,
      additionalInstructions: inspectionPlan.additionalInstructions,
      isActive: inspectionPlan.isActive,
      isLocked: inspectionPlan.isLocked,
      isLineItemActive: lineItem.isActive,
      attachments: inspectionPlan.attachments ?? [],
      majorRequirement: lineItem.majorRequirement,
      relatedInfo: lineItem.relatedInfo || '',
      estimatedQuantity: lineItem.estimatedQuantity || '',
      performanceStandard: lineItem.performanceStandard || '',
      autoInspections: inspectionPlan.autoInspections ? {
        normalFrequencyId: inspectionPlan.autoInspections.NormalFrequencyId || 0,
        normalLow: inspectionPlan.autoInspections.NormalLow,
        normalHigh: inspectionPlan.autoInspections.NormalHigh,
        decreasedFrequencyId: inspectionPlan.autoInspections.DecreasedFrequencyId || 0,
        decreasedLow: inspectionPlan.autoInspections.DecreasedLow,
        increasedFrequencyId: inspectionPlan.autoInspections.IncreasedFrequencyId || 0,
        increasedHigh: inspectionPlan.autoInspections.IncreasedHigh,
      } : undefined,
      fixedInspections: inspectionPlan.fixedInspections?.map(fi => ({
        date: fi.Date,
        isAnnual: fi.IsAnnual,
        site: fi.Site
      })) || [],
      checklistItems: inspectionPlan.checklistItems?.map((item: any) => ({
        id: item.Id,
        text: item.Text,
        weight: parseFloat(item.Weight) || 1,
        resultsAnalysis: item.ResultsAnalysis
      })) || [],
      scheduledInspections: inspectionPlan.scheduledInspections?.map(si => ({
        id: si.Id,
        site: si.Site || '',
        date: si.Date || '',
        frequencyId: si.FrequencyId,
      })) || [],
    });
    setInspectionPlanModalOpen(true);
  };

  const handleViewLineItem = (lineItem: PlanningLineItem, groupIdParam?: number) => {

    let groupId = groupIdParam;
    if (!groupId) {
      for (const group of data) {
        if (group.lineItems?.some(li => li.id === lineItem.id)) {
          groupId = group.id;
          break;
        }
      }
    }
    
    const group = data.find(g => g.id === groupId);
    if (!group) return;

    const canEdit = permissionService.canEditLineItem();
    setLineItemModalMode(canEdit ? 'edit' : 'view');
    setSelectedLineItemData({
      id: lineItem.id,
      lineItem: lineItem.lineItem,
      lastUpdated: lineItem.lastUpdated,
      title: lineItem.title,
      groupId: groupId,
      paragraph: lineItem.lineItem.split('.')[1] || '',
      subParagraph: lineItem.lineItem.split('.')[2] || '',
      remainder: lineItem.lineItem.split('.')[3] || '',
      majorRequirement: lineItem.majorRequirement,
      relatedInfo: lineItem.relatedInfo,
      estimatedQuantity: lineItem.estimatedQuantity,
      performanceStandard: lineItem.performanceStandard,
      modificationStatus: lineItem.modificationStatus,
      attachmentCount: lineItem.attachmentCount,
      isActive: lineItem.isActive,
      attachments: lineItem.attachments ?? [],
    });
    setLineItemModalOpen(true);
  };



  function renderInspectionPlan(inspectionPlan: InspectionPlanItem | null) {
    if (!inspectionPlan) {
      return (
        <div style={{
          padding: '1rem 1rem 1rem 2rem',
          textAlign: 'center',
          color: 'var(--ion-color-medium)',
          fontStyle: 'italic',
          background: 'var(--ion-color-light-tint)',
          borderLeft: '2px solid var(--ion-color-medium-tint)'
        }}>
          No inspection plan configured for this line item.
        </div>
      );
    }

    return (
      <div 
        style={{
          padding: '1rem 1rem 1rem 2rem',
          background: 'var(--ion-color-light-tint)',
          borderLeft: '2px solid var(--ion-color-secondary-tint)'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '0.75rem'
        }}>
          <div style={{ fontWeight: '600'}}>
            Inspection Plan Summary
          </div>
          {
            !inspectionPlan.isActive && (
              <div style={{ color: 'var(--ion-color-warning)'}}>
                <IonIcon icon={banOutline} title="Inactive" aria-label="Inactive" />
              </div>
            )
          } 
          {inspectionPlan.isLocked && (
            <div style={{ 
              fontSize: '0.8rem',
              color: 'var(--ion-color-warning)',
              fontWeight: '600'
            }}>
              🔒 
            </div>
          )}
          <IonButton
            fill="clear"
            size="small"
            onClick={() => handleInspectionPlanEdit(inspectionPlan.id)}
            style={{ margin: 0 }}
          >
            <IonIcon icon={createOutline} style={{ fontSize: '16px' }} />
          </IonButton>
        </div>
        
        {/* Inspection plan summary */}
        <div 
          style={{
            padding: '1rem',
            background: 'var(--ion-color-light-tint)',
            borderRadius: '8px',
            border: '1px solid var(--ion-color-light-shade)',
            color: 'var(--ion-color-dark)',
            fontSize: '0.85rem',
            lineHeight: '1.4'
          }}
        >
          {/* Checklist Summary */}
          <div style={{ marginBottom: '8px' }}>
            <strong>Checklist:</strong> {inspectionPlan.checklistItems?.length || 0} items
          </div>
          
          {/* Frequency Summary */}
          <div style={{ marginBottom: '8px' }}>
            <strong>Frequency:</strong> {inspectionPlan.autoInspections ? (
              `Auto-adjusted (Normal/Decreased/Increased levels configured)`
            ) : inspectionPlan.fixedInspections?.length ? (
              `${inspectionPlan.fixedInspections.length} fixed dates`
            ) : (
              'Not configured'
            )}
          </div>
          
          {/* Inspection Background Summary */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '0.8rem' }}>
            {inspectionPlan.expertiseAreaId != null && (
              <span><strong>Area of Expertise:</strong> {lookupDataService.getExpertiseAreas()?.get(inspectionPlan.expertiseAreaId)?.Name ?? `#${inspectionPlan.expertiseAreaId}`}</span>
            )}
            {inspectionPlan.primaryValue && (
              <span><strong>Primary:</strong> {inspectionPlan.primaryValue}</span>
            )}
            {inspectionPlan.alternateValue && (
              <span><strong>Alternate:</strong> {inspectionPlan.alternateValue}</span>
            )}
            {inspectionPlan.timeToInspect && (
              <span><strong>Hours:</strong> {inspectionPlan.timeToInspect}</span>
            )}
          </div>
          
          {/* Sample Information */}
          {(inspectionPlan.sampleSize || inspectionPlan.sampleSelection) && (
            <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>
              {inspectionPlan.sampleSize && (
                <div><strong>Sample Size:</strong> {truncateText(inspectionPlan.sampleSize, 40)}</div>
              )}
              {inspectionPlan.sampleSelection && (
                <div><strong>Sample Selection:</strong> {truncateText(inspectionPlan.sampleSelection, 50)}</div>
              )}
            </div>
          )}
          
          {/* Additional Instructions */}
          {inspectionPlan.additionalInstructions && (
            <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>
              <strong>Instructions:</strong> {truncateText(inspectionPlan.additionalInstructions, 60)}
            </div>
          )}
          
        </div>

        {/* Scheduled Inspections */}
        <div style={{
          marginTop: '0.75rem',
          padding: '1rem',
          background: 'var(--ion-color-light-tint)',
          borderRadius: '8px',
          border: '1px solid var(--ion-color-light-shade)',
          fontSize: '0.85rem'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
            Scheduled Inspections
          </div>

          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '0.5rem',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--ion-color-medium)',
            borderBottom: '1px solid var(--ion-color-light-shade)',
            paddingBottom: '0.4rem',
            marginBottom: '0.25rem'
          }}>
            <div>Inspection Date</div>
            <div>Site</div>
          </div>

          {/* Rows */}
          {!inspectionPlan.scheduledInspections || inspectionPlan.scheduledInspections.length === 0 ? (
            <div style={{ color: 'var(--ion-color-medium)', fontStyle: 'italic', padding: '0.4rem 0' }}>
              No scheduled inspections
            </div>
          ) : (
            inspectionPlan.scheduledInspections.map((si, idx) => (
              <div
                key={si.Id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.5rem',
                  padding: '0.4rem 0',
                  borderBottom: idx < inspectionPlan.scheduledInspections!.length - 1
                    ? '1px solid var(--ion-color-light-shade)'
                    : 'none',
                  fontSize: '0.8rem'
                }}
              >
                <div>{si.Date ? new Date(si.Date).toLocaleDateString() : '—'}</div>
                <div>{si.Site || '—'}</div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  const visibleLineItemIds = getVisibleLineItemIds(viewData);
  const selectedVisibleLineItemCount = getSelectedVisibleLineItemCount(visibleLineItemIds, selection.selectedLineItems);

  const selectAllVisibleLineItems = (checked: boolean) => {
    setActionAccordionRows((currentRows) => visibleLineItemIds.reduce(
      (rows, rowId) => setRowSelection(rows, rowId, checked, 'cascade-visible'),
      currentRows,
    ));
  };

  const bulkActions: BulkActionsBarAction[] = [
    ...(lookupReady && permissionService.canActivateLineItem() ? [
      {
        id: 'activate-line-items', label: 'Activate', icon: checkmarkCircleOutline,
        group: 1, order: 1, disabled: selectedVisibleLineItemCount === 0,
        loading: runningHeaderActionId === 'activate-line-items',
        confirm: { header: 'Activate Line Items', message: 'Activate the selected line items?', confirmLabel: 'Activate' },
        onExecute: () => handleBulkAction('activate-line-items'),
      },
      {
        id: 'deactivate-line-items', label: 'Inactivate', icon: banOutline,
        group: 1, order: 2, disabled: selectedVisibleLineItemCount === 0,
        loading: runningHeaderActionId === 'deactivate-line-items',
        confirm: { header: 'Inactivate Line Items', message: 'You will be asked to provide a reason after confirmation.', confirmLabel: 'Continue' },
        onExecute: () => handleBulkAction('deactivate-line-items'),
      },
    ] : []),
    ...(lookupReady && permissionService.canActivateInspectionPlan(false) ? [
      {
        id: 'activate-inspection-plans', label: 'Activate', icon: checkmarkCircleOutline,
        group: 2, order: 1, disabled: selectedVisibleLineItemCount === 0,
        loading: runningHeaderActionId === 'activate-inspection-plans',
        confirm: { header: 'Activate Inspection Plans', message: 'Activate the inspection plans for the selected line items?', confirmLabel: 'Activate' },
        onExecute: () => handleBulkAction('activate-inspection-plans'),
      },
      {
        id: 'deactivate-inspection-plans', label: 'Inactivate', icon: banOutline,
        group: 2, order: 2, disabled: selectedVisibleLineItemCount === 0,
        loading: runningHeaderActionId === 'deactivate-inspection-plans',
        confirm: { header: 'Inactivate Inspection Plans', message: 'Inactivate the inspection plans for the selected line items?', confirmLabel: 'Inactivate' },
        onExecute: () => handleBulkAction('deactivate-inspection-plans'),
      },
    ] : []),
    ...(lookupReady && permissionService.canLockInspectionPlan(true) ? [{
      id: 'lock-inspection-plans', label: 'Lock', icon: lockClosedOutline,
      group: 2, order: 3, disabled: selectedVisibleLineItemCount === 0,
      loading: runningHeaderActionId === 'lock-inspection-plans',
      confirm: { header: 'Lock Inspection Plans', message: 'Lock the inspection plans for the selected line items?', confirmLabel: 'Lock' },
      onExecute: () => handleBulkAction('lock-inspection-plans'),
    }] : []),
    ...(lookupReady && permissionService.canUnlockInspectionPlan(true) ? [{
      id: 'unlock-inspection-plans', label: 'Unlock', icon: lockOpenOutline,
      group: 2, order: 4, disabled: selectedVisibleLineItemCount === 0,
      loading: runningHeaderActionId === 'unlock-inspection-plans',
      confirm: { header: 'Unlock Inspection Plans', message: 'Unlock the inspection plans for the selected line items?', confirmLabel: 'Unlock' },
      onExecute: () => handleBulkAction('unlock-inspection-plans'),
    }] : []),
  ];

  const bulkActionsGroupLabels: Record<number, string> = {
    1: 'Line Items',
    2: 'Inspection Plans',
  };

  const canToggleView = !isMobile && !Capacitor.isNativePlatform();
  const effectiveViewMode: PlanningViewMode = canToggleView ? viewMode : 'accordion';

  const handleViewModeChange = (mode: PlanningViewMode) => {
    setViewMode(mode);
    setStoredPlanningViewMode(mode);
  };

  const handleGridEditGroup = (row: PlanningGroupGridRow) => {
    const group = data.find((candidate) => candidate.id === row.id);
    if (group) {
      handleViewGroup(group);
    }
  };

  const handleGridEditLineItem = (row: PlanningLineItemGridRow) => {
    const group = data.find((candidate) => candidate.id === row.groupId);
    const lineItem = group?.lineItems?.find((candidate) => candidate.id === row.id);
    if (lineItem) {
      handleViewLineItem(lineItem, row.groupId);
    }
  };

  const handleGridAddLineItem = (groupId?: number) => {
    handleAddLineItem(groupId ?? 0);
  };

  const handleGridEditInspectionPlan = (row: PlanningInspectionPlanGridRow) => {
    handleInspectionPlanEdit(row.id);
  };

  const handleGridActivateLineItems = async (ids: number[]) => {
    if (ids.length === 0) {
      setToastMessage('No line items selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    try {
      const result = await planningService.activateLineItems(ids);
      if (result.failed.length === 0) {
        setToastMessage(`${result.successIds.length} line item(s) activated`);
        setToastVariant('success');
      } else {
        setToastMessage(`Activated ${result.successIds.length}/${ids.length}. Failed: ${result.failed.length}`);
        setToastVariant('warning');
      }
      setShowToast(true);
      await refreshPlanningData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleGridDeactivateLineItemsRequest = (ids: number[]) => {
    if (ids.length === 0) {
      setToastMessage('No line items selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    setPendingDeactivateIds(ids);
    setShowDeactivateConfirm(true);
  };

  const handleGridActivateInspectionPlans = async (ids: number[]) => {
    if (ids.length === 0) {
      setToastMessage('No inspection plans selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    try {
      await apiService.activateDeactivateInspectionPlan(ids, []);
      setToastMessage(`${ids.length} inspection plan(s) activated`);
      setToastVariant('success');
      setShowToast(true);
      await refreshPlanningData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleGridDeactivateInspectionPlans = async (ids: number[]) => {
    if (ids.length === 0) {
      setToastMessage('No inspection plans selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    try {
      await apiService.activateDeactivateInspectionPlan([], ids);
      setToastMessage(`${ids.length} inspection plan(s) deactivated`);
      setToastVariant('success');
      setShowToast(true);
      await refreshPlanningData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleGridLockInspectionPlans = async (ids: number[]) => {
    if (ids.length === 0) {
      setToastMessage('No inspection plans selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    try {
      await apiService.lockUnlockInspectionPlan(ids, [], '');
      setToastMessage(`${ids.length} inspection plan(s) locked`);
      setToastVariant('success');
      setShowToast(true);
      await refreshPlanningData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleGridUnlockInspectionPlans = async (ids: number[]) => {
    if (ids.length === 0) {
      setToastMessage('No inspection plans selected');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    try {
      await apiService.lockUnlockInspectionPlan([], ids, '');
      setToastMessage(`${ids.length} inspection plan(s) unlocked`);
      setToastVariant('success');
      setShowToast(true);
      await refreshPlanningData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk action failed';
      setToastMessage(errorMessage);
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const iconActions: PageHeaderIconAction[] = [
    ...(permissionService.canAddGroup()
      ? [{
          kind: 'icon' as const,
          group: 1,
          order: 1,
          id: 'planning-new-group',
          label: 'New Group',
          icon: add,
          requiresConfirm: false,
          onExecute: handleAddGroup,
        }]
      : []),
    ...(canToggleView
      ? [{
          kind: 'icon' as const,
          group: 2,
          order: 1,
          id: 'planning-switch-to-grid',
          label: 'Switch to Grid View',
          icon: gridOutline,
          requiresConfirm: false,
          onExecute: () => handleViewModeChange('grid'),
        }]
      : []),
  ];

  return (
    <PageLayout>
      
      <IonContent fullscreen className="ion-padding">
        <div style={{ maxWidth: effectiveViewMode === 'grid' ? '100%' : '1200px', margin: '0 auto' }}>
          {effectiveViewMode === 'grid' ? (
            <PlanningGridView
              groups={data}
              canAddGroup={lookupReady && permissionService.canAddGroup()}
              canAddLineItem={canAddLineItem}
              canActivateLineItem={lookupReady && permissionService.canActivateLineItem()}
              canActivateInspectionPlan={lookupReady && permissionService.canActivateInspectionPlan(false)}
              canLockInspectionPlan={lookupReady && permissionService.canLockInspectionPlan(true)}
              canUnlockInspectionPlan={lookupReady && permissionService.canUnlockInspectionPlan(true)}
              onEditGroup={handleGridEditGroup}
              onAddGroup={handleAddGroup}
              onEditLineItem={handleGridEditLineItem}
              onAddLineItem={handleGridAddLineItem}
              onActivateLineItems={(ids) => void handleGridActivateLineItems(ids)}
              onDeactivateLineItems={handleGridDeactivateLineItemsRequest}
              onEditInspectionPlan={handleGridEditInspectionPlan}
              onActivateInspectionPlans={(ids) => void handleGridActivateInspectionPlans(ids)}
              onDeactivateInspectionPlans={(ids) => void handleGridDeactivateInspectionPlans(ids)}
              onLockInspectionPlans={(ids) => void handleGridLockInspectionPlans(ids)}
              onUnlockInspectionPlans={(ids) => void handleGridUnlockInspectionPlans(ids)}
              onSwitchToAccordion={() => handleViewModeChange('accordion')}
            />
          ) : (
            <>
              <PageHeaderBar
                filters={{
                  controls: filterControls,
                  summaryText: buildPlanningFilterSummary(filters),
                  onApply: (nextControls) => {
                    const nextFilters = mapPlanningControlsToFilters(nextControls, filters);
                    setFilters(nextFilters);
                  },
                  applyLabel: 'Apply',
                }}
                search={{
                  value: searchQuery,
                  placeholder: 'Search groups and line items',
                  debounceMs: 300,
                  onSearch: setSearchQuery,
                }}
                actions={{
                  iconActions,
                  runningActionId: runningHeaderActionId,
                }}
                collapsible={isMobile}
              />

              <div style={{ marginLeft: '70px' }}> 
              <BulkActionsBar
                selectedCount={selectedVisibleLineItemCount}
                totalCount={visibleLineItemIds.length}
                onSelectAll={selectAllVisibleLineItems}
                actions={bulkActions}
                groupLabels={bulkActionsGroupLabels}
              />
              </div>
              <div>
                {renderContent()}
              </div>
            </>
          )}
        </div>

        <Toaster
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          variant={toastVariant}
        />

        <IonAlert
          isOpen={showDeactivateConfirm}
          onDidDismiss={() => {
            setShowDeactivateConfirm(false);
          }}
          header="Inactivate Line Items"
          message={`Inactivate ${pendingDeactivateIds.length} selected line item(s). Please provide a reason.`}
          inputs={[
            {
              name: 'reason',
              type: 'textarea',
              placeholder: 'Enter reason',
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                setPendingDeactivateIds([]);
              },
            },
            {
              text: 'Confirm',
              handler: (alertData) => {
                const reason = String(alertData?.reason ?? '').trim();
                if (!reason) {
                  setToastMessage('Reason is required to inactivate line items');
                  setToastVariant('warning');
                  setShowToast(true);
                  return false;
                }

                void handleConfirmDeactivateLineItems(reason);
                return true;
              },
            },
          ]}
        />
        
        <GroupModal
          isOpen={groupModalOpen}
          mode={groupModalMode}
          groupData={selectedGroupData}
          existingGroups={mapPlanningGroupsToGroupData(data)}
          onDidDismiss={handleGroupModalClose}
          onSave={handleGroupSave}
        />
        
        <LineItemModal
          isOpen={lineItemModalOpen}
          mode={lineItemModalMode}
          initialData={selectedLineItemData}
          availableGroups={mapPlanningGroupsToAvailableGroups(data)}
          existingFiles={existingFiles}
          onClose={handleLineItemModalClose}
          onSave={handleLineItemSave}
          onRefresh={refreshPlanningData}
        />
        
        <InspectionPlanModal
          isOpen={inspectionPlanModalOpen}
          mode={inspectionPlanModalMode}
          initialData={selectedInspectionPlanData}
          existingFiles={existingFiles}
          onClose={handleInspectionPlanModalClose}
          onSave={handleInspectionPlanSave}
          onRefresh={refreshPlanningData}
        />
      </IonContent>
    </PageLayout>
  );
};

export default Planning;